import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getToastErrorMessage,
  ILogyxErrorAnswer,
  isAnswerLost,
  LogyxError,
  LogyxErrorId,
  readLogyxError,
} from 'services/api/errors';
import {
  captureLostAnswer,
  LostAnswerEndpoint,
} from 'services/api/lostAnswerCapture';
import { useOrderRefusal } from './useOrderRefusal';

// What Finish order does with each answer of POST /v1/carts/order (logix-core
// docs/ordering.md, "The answer"; docs/BP-1219-ordering.md). Every answer that
// saved an order names it in `order_id`, a 409 too, and Forms opens that order:
// its page shows how it stands, and asks again while Logyx core settles it.
// The fields of a successful cart order's answer that Forms reads.
interface ICartOrderAnswer {
  order_id?: number;
  replayed?: boolean;
  internal_number?: string | null;
  // The connector's own field for the supplier's number.
  order?: string;
}

// Reads the cart Finish order sent (GET /v1/carts/<id>); rejects as the request
// does.
export type CartReader = (cartId: string) => Promise<unknown>;

// What a 404 of Finish order means. Logyx core deletes a cart once its order
// went through, so a cart that is not found may have been ordered. But Logyx
// core also answers 404 for an empty cart, a shipping address, a company or
// relation, or a connector not found: all its NotFound (A0:019), each with a
// message translated into the user's language, so neither the id nor the text
// tells them apart. Forms asks for the cart instead:
// - cartGone: the cart is not found either; its order may have gone through;
// - notPlaced: the cart is still there (or the 404 is not a NotFound), so
//   nothing was ordered, and Logyx core's message (`reason`) says why;
// - unknown: the cart could not be read, so either may be true.
export interface ICartOrderNotFound {
  kind: 'cartGone' | 'notPlaced' | 'unknown';
  reason?: string;
}

// Never rejects: Finish order's onError awaits it (see readLogyxError).
export const readCartOrderNotFound = async (
  answer: ILogyxErrorAnswer,
  cartId: string | undefined,
  readCart: CartReader
): Promise<ICartOrderNotFound> => {
  const first = answer.errors[0];
  const message = first && typeof first === 'object' ? first.message : first;
  const notPlaced: ICartOrderNotFound =
    typeof message === 'string' && message
      ? { kind: 'notPlaced', reason: message }
      : { kind: 'notPlaced' };

  if (answer.errorId !== LogyxErrorId.NOT_FOUND) {
    return notPlaced;
  }
  if (!cartId) {
    return { kind: 'unknown' };
  }
  try {
    await readCart(cartId);
    return notPlaced;
  } catch (error) {
    const cart = readLogyxError(error);
    return cart.isLogyxAnswer &&
      cart.status === 404 &&
      cart.errorId === LogyxErrorId.NOT_FOUND
      ? { kind: 'cartGone' }
      : { kind: 'unknown' };
  }
};

export const useCartOrderAnswers = (
  singleOrderPath: string,
  ordersPath: string,
  onNoAnswer: () => void,
  readCart: CartReader
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // An answer that names an order: its toast, and its page (useOrderRefusal).
  const openRefusedOrder = useOrderRefusal(singleOrderPath, 'finishOrder');

  const onSuccess = (data: ICartOrderAnswer | undefined) => {
    if (data?.replayed) {
      const number = data.internal_number || data.order;
      toast.info(
        number
          ? t(
              'Already created as {{number}}. This order was not sent to the supplier again.',
              { number }
            )
          : t('This order was already created. It was not sent again.')
      );
    }
    if (data?.order_id) {
      navigate(singleOrderPath.replace(':id', String(data.order_id)));
    } else {
      navigate(ordersPath);
    }
  };

  // react-query passes the request's body as the second argument.
  const onError = async (error: unknown, sent?: { cart_id?: string }) => {
    if (openRefusedOrder(error)) {
      return;
    }

    const answer = readLogyxError(error);
    // Each answer that leaves the order in doubt sends one Sentry event, with
    // the request id (lostAnswerCapture.ts).
    const captureInDoubt = () =>
      captureLostAnswer(error, {
        endpoint: LostAnswerEndpoint.CART_ORDER,
        cartId: sent?.cart_id,
      });

    // No answer that says what happened (504, a network error): the order may
    // have gone through, so Finish order must not simply be pressed again.
    if (isAnswerLost(answer)) {
      captureInDoubt();
      onNoAnswer();
      return;
    }

    if (answer.status === 404) {
      const notFound = await readCartOrderNotFound(
        answer,
        sent?.cart_id,
        readCart
      );
      // The cart is gone. An order that went through deletes the cart it
      // emptied, but so do Create new cart and a quotation made from the cart,
      // and Forms cannot tell which: it may have been ordered.
      if (notFound.kind === 'cartGone') {
        captureInDoubt();
        toast.info(
          t(
            'This cart no longer exists. It may have been ordered: check your orders.'
          )
        );
        navigate(ordersPath);
      } else if (notFound.kind === 'unknown') {
        captureInDoubt();
        onNoAnswer();
      } else {
        toast.error(
          notFound.reason
            ? t('The order could not be placed: {{reason}}', {
                reason: notFound.reason,
              })
            : t('The order could not be placed.')
        );
      }
      return;
    }

    toast.error(getToastErrorMessage(error as LogyxError));
  };

  return { onSuccess, onError };
};
